/**
 * DecaissementsPanel — LOT 4 : sorties comptables (la mairie règle un
 * fournisseur). Saisie manuelle + pièce jointe (facture reçue), ventilation en
 * dépense sur un poste. Alimente le livre de comptabilité (colonne sorties).
 * Onglet « Dépenses » de la brique compta.
 */
import { useEffect, useState , type ReactNode } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { Decaissement, DecaissementBody, DecaissementMoyen, ComptaPoste, FactureAEncaisser } from '@/lib/api';
import type { NdfTiers } from '@roronoa-collectivites/shared-types';
import { Plus, Trash2, ArrowUpRight } from 'lucide-react';

// Wrapper léger (le composant Field du design-system impose htmlFor).
function FieldBox({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-medium text-ink-muted">{label}</label>
      {children}
    </div>
  );
}


const MOYENS: { k: DecaissementMoyen; label: string }[] = [
  { k: 'virement', label: 'Virement' },
  { k: 'cheque', label: 'Chèque' },
  { k: 'prelevement', label: 'Prélèvement' },
  { k: 'cb', label: 'Carte bancaire' },
  { k: 'espece', label: 'Espèces' },
];

function euro(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function frDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}

export function DecaissementsPanel({ postes }: { postes: ComptaPoste[] }) {
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<Decaissement[]>([]);
  const [tiers, setTiers] = useState<NdfTiers[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Formulaire
  const [objet, setObjet] = useState('');
  const [montant, setMontant] = useState('');
  const [moyen, setMoyen] = useState<DecaissementMoyen>('virement');
  const [reference, setReference] = useState('');
  const [tiersId, setTiersId] = useState('');
  const [fournisseurNom, setFournisseurNom] = useState('');
  const [posteId, setPosteId] = useState('');
  const [pieceJointeUrl, setPieceJointeUrl] = useState('');
  const [dateDecaissement, setDateDecaissement] = useState(() => new Date().toISOString().slice(0, 10));

  // Sélecteur « facture à payer » (factures de dépense émises, ex. recharge SMS OFAD)
  const [facturesAPayer, setFacturesAPayer] = useState<FactureAEncaisser[]>([]);
  const [factureId, setFactureId] = useState<string | null>(null);
  const [factureQuery, setFactureQuery] = useState('');
  const [openFactures, setOpenFactures] = useState(false);

  async function charger() {
    try {
      const [d, t] = await Promise.all([api.comptabilite.decaissements.list(), api.notesFrais.tiers.list()]);
      setItems(d.decaissements);
      setTiers(t);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement.'); }
  }
  useEffect(() => { void charger(); }, []);

  // Recherche des factures à payer (dépenses émises non réglées).
  useEffect(() => {
    let stop = false;
    if (!showForm) return;
    (async () => {
      try {
        const r = await api.comptabilite.decaissements.facturesAPayer(factureQuery || undefined);
        if (!stop) setFacturesAPayer(r.factures);
      } catch { /* silencieux : le sélecteur reste optionnel */ }
    })();
    return () => { stop = true; };
  }, [factureQuery, showForm]);

  function reset() {
    setObjet(''); setMontant(''); setMoyen('virement'); setReference('');
    setTiersId(''); setFournisseurNom(''); setPosteId(''); setPieceJointeUrl('');
    setDateDecaissement(new Date().toISOString().slice(0, 10));
    setFactureId(null); setFactureQuery(''); setOpenFactures(false);
  }

  // Sélection d'une facture à payer → pré-remplit objet + montant + lie l'id.
  function choisirFacture(f: FactureAEncaisser) {
    setFactureId(f.id);
    setObjet(`Règlement facture ${f.numero}${f.objet ? ` — ${f.objet}` : ''}`);
    setMontant(String(f.montantTtc));
    setFournisseurNom(f.clientNom ?? 'One For All Distribution');
    setOpenFactures(false);
    setFactureQuery('');
  }

  async function enregistrer() {
    setError(null);
    const m = parseFloat(montant.replace(',', '.'));
    if (!objet.trim()) { setError("L'objet est obligatoire."); return; }
    if (!m || m <= 0) { setError('Le montant doit être supérieur à 0.'); return; }
    setBusy(true);
    const body: DecaissementBody = {
      objet: objet.trim(),
      montant: m,
      moyen,
      reference: reference.trim() || null,
      tiersId: tiersId || null,
      fournisseurNom: tiersId ? null : (fournisseurNom.trim() || null),
      posteId: posteId || null,
      pieceJointeUrl: pieceJointeUrl.trim() || null,
      dateDecaissement: dateDecaissement || null,
      factureId: factureId || null,
    };
    try {
      await api.comptabilite.decaissements.create(body);
      reset(); setShowForm(false); await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : "Impossible d'enregistrer la dépense.");
    } finally { setBusy(false); }
  }

  async function supprimer(d: Decaissement) {
    const ok = await confirm({
      title: 'Supprimer cette dépense ?',
      description: `« ${d.objet} » (${euro(d.montant)}) sera retirée du livre de comptabilité.`,
      confirmLabel: 'Supprimer', cancelLabel: 'Annuler', danger: true,
    });
    if (!ok) return;
    try { await api.comptabilite.decaissements.remove(d.id); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible.'); }
  }

  const totalMois = items
    .filter((d) => new Date(d.dateDecaissement).getMonth() === new Date().getMonth() && new Date(d.dateDecaissement).getFullYear() === new Date().getFullYear())
    .reduce((s, d) => s + d.montant, 0);

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-ink-muted">Règlements aux fournisseurs (sorties). Ajoutez la facture reçue en pièce jointe.</p>
          <p className="text-xs text-ink-subtle">Dépensé ce mois : <strong className="tabular-nums text-ink">{euro(totalMois)}</strong></p>
        </div>
        {!showForm && (
          <Button variant="compta" onClick={() => { reset(); setShowForm(true); }}>
            <Plus className="mr-1 h-4 w-4" /> Nouvelle dépense
          </Button>
        )}
      </div>

      {showForm && (
        <Card variant="glass" padded>
          {/* Sélecteur : régler une facture émise (dépense, ex. recharge SMS OFAD) */}
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              Facture à régler <span className="text-ink-subtle">(optionnel — ex. recharge SMS)</span>
            </label>
            {factureId ? (
              <div className="flex items-center justify-between rounded-md border border-compta/40 bg-compta/10 px-3 py-2 text-sm">
                <span className="text-ink">Facture liée — le montant sera marqué payé.</span>
                <button type="button" className="text-xs text-ink-muted underline"
                  onClick={() => { setFactureId(null); }}>
                  Retirer
                </button>
              </div>
            ) : (
              <div className="relative">
                <input type="text" value={factureQuery}
                  onFocusCapture={() => setOpenFactures(true)}
                  onChange={(e) => { setFactureQuery(e.target.value); setOpenFactures(true); }}
                  className="h-9 w-full rounded-md border border-border bg-surface/40 px-2 text-sm text-ink"
                  placeholder="Rechercher une facture à payer (n°, objet, montant)…" />
                {openFactures && facturesAPayer.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-surface shadow-lg">
                    {facturesAPayer.map((f) => (
                      <button key={f.id} type="button"
                        onClick={() => choisirFacture(f)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-compta/10">
                        <span>
                          <span className="font-medium text-ink">{f.numero}</span>
                          {f.objet && <span className="text-ink-muted"> · {f.objet}</span>}
                        </span>
                        <span className="tabular-nums text-ink">{euro(f.montantTtc)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {openFactures && facturesAPayer.length === 0 && (
                  <p className="mt-1 text-xs text-ink-subtle">Aucune facture à payer en attente.</p>
                )}
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <FieldBox label="Objet de la dépense">
              <input type="text" value={objet} onChange={(e) => setObjet(e.target.value)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" placeholder="Ex. Fournitures voirie" />
            </FieldBox>
            <FieldBox label="Montant (€)">
              <input type="number" min={0} step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-right text-sm text-ink" placeholder="0.00" />
            </FieldBox>
            <FieldBox label="Moyen de paiement">
              <select value={moyen} onChange={(e) => setMoyen(e.target.value as DecaissementMoyen)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink">
                {MOYENS.map((m) => <option key={m.k} value={m.k}>{m.label}</option>)}
              </select>
            </FieldBox>
            <FieldBox label="Date">
              <input type="date" value={dateDecaissement} onChange={(e) => setDateDecaissement(e.target.value)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" />
            </FieldBox>
            <FieldBox label="Fournisseur (annuaire des tiers)">
              <select value={tiersId} onChange={(e) => setTiersId(e.target.value)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink">
                <option value="">— Fournisseur libre —</option>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}
              </select>
            </FieldBox>
            {!tiersId && (
              <FieldBox label="Nom du fournisseur (libre)">
                <input type="text" value={fournisseurNom} onChange={(e) => setFournisseurNom(e.target.value)}
                  className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" placeholder="Ex. SARL Dupont" />
              </FieldBox>
            )}
            <FieldBox label="Poste de dépense (ventilation)">
              <select value={posteId} onChange={(e) => setPosteId(e.target.value)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink">
                <option value="">— Non ventilé —</option>
                {postes.filter((p) => p.actif).map((p) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
              </select>
            </FieldBox>
            <FieldBox label="Référence (n° facture reçue)">
              <input type="text" value={reference} onChange={(e) => setReference(e.target.value)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" placeholder="Ex. FACT-2026-0142" />
            </FieldBox>
            <FieldBox label="Pièce jointe (lien facture reçue)" className="sm:col-span-2">
              <input type="text" value={pieceJointeUrl} onChange={(e) => setPieceJointeUrl(e.target.value)}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" placeholder="URL du document (upload R2 — à venir)" />
            </FieldBox>
          </div>
          <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="ghost" onClick={() => { setShowForm(false); setError(null); }}>Annuler</Button>
            <Button variant="compta" disabled={busy} onClick={() => void enregistrer()}>{busy ? 'Enregistrement…' : 'Enregistrer la dépense'}</Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="border-b border-border px-4 py-3">
          <h2 className="font-display text-sm font-semibold text-ink">Dépenses enregistrées</h2>
        </div>
        {items.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-subtle">Aucune dépense enregistrée.</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <ArrowUpRight className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink">{d.objet}</span>
                    {d.posteLibelle && <Badge variant="info" size="sm">{d.posteLibelle}</Badge>}
                  </div>
                  <div className="text-xs text-ink-subtle">
                    {frDate(d.dateDecaissement)} · {d.fournisseurNom ?? 'Fournisseur libre'}
                    {d.reference ? ` · ${d.reference}` : ''} · {MOYENS.find((m) => m.k === d.moyen)?.label ?? d.moyen}
                  </div>
                </div>
                <div className="text-right text-sm font-semibold tabular-nums text-danger">− {euro(d.montant)}</div>
                <button type="button" onClick={() => void supprimer(d)} className="text-ink-subtle hover:text-danger" title="Supprimer">
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {dialogElement}
    </div>
  );
}
